import {
	Alert,
	Button,
	Checkbox,
	FormControl,
	FormControlLabel,
	FormLabel,
	InputLabel,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
	Snackbar
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import axios from 'axios';
import React, { useEffect, useState } from 'react';

interface PushContentsTabProps {
	appContext: any;
	setAppContext: (ctx: any) => void;
}

const PushContentsTab: React.FC<PushContentsTabProps> = ({ appContext, setAppContext }) => {
	const [contentType, setContentType] = useState<'academy' | 'menu'>('academy');
	const [pushType, setPushType] = useState<'careteam' | 'patients'>('careteam');
	const [careteams, setCareteams] = useState<string[]>([]);
	const [diseases, setDiseases] = useState<string[]>([]);
	const [patients, setPatients] = useState<string[]>([]);
	const [selectedCareteam, setSelectedCareteam] = useState('');
	const [selectedDisease, setSelectedDisease] = useState('');
	const [selectedContentId, setSelectedContentId] = useState('');
	const [selectedContentIdFull, setSelectedContentIdFull] = useState('');
	const [availableContents, setAvailableContents] = useState<any[]>([]);
	const [loadingContents, setLoadingContents] = useState(false);
	const [selectedPatients, setSelectedPatients] = useState<string[]>([]);
	const [loadingHierarchy, setLoadingHierarchy] = useState(false);
	const [submitLoading, setSubmitLoading] = useState(false);
	const [snackbar, setSnackbar] = useState<{open: boolean, message: string, severity: 'success'|'error'}>({open: false, message: '', severity: 'success'});

	// Fetch available contents when careteam/disease/contentType changes
	useEffect(() => {
		if (selectedCareteam && selectedDisease && appContext.selectedProviderId) {
			setLoadingContents(true);
			axios.get(
				`${process.env.REACT_APP_AVAILABLE_CONTENTS_API_BASE_URL}/api/${appContext.selectedProviderId.toUpperCase()}/${selectedDisease}/available-contents?contentType=${contentType}`,
				{
					headers: {
						'authorization': sessionStorage.getItem('adminToken') as string
					}
				}
			)
			.then(res => {
				let options: any[] = [];
				if (contentType === 'academy') {
					const books = res.data.books_and_chapters || [];
					books.forEach((book: any) => {
						options.push({
							id: book.bookId,
							label: book.title?.en || book.title?.nl || book.bookId,
							type: 'book',
						});
						if (Array.isArray(book.chapters) && book.chapters.length > 0) {
							book.chapters.forEach((chap: any) => {
								options.push({
									id: chap.bookMetaId.replace('CHAPTER|', ''),
									label: (chap.title?.en || chap.title?.nl || chap.bookMetaId) + ` (chapter)` ,
									type: 'chapter',
									parentBook: book.bookId,
								});
							});
						} else {
							options.push({
								id: `no-chapter-${book.bookId}`,
								label: '---No Chapter. Check Contentful---',
								type: 'no-chapter',
								parentBook: book.bookId,
								disabled: true,
							});
						}
					});
				} else if (contentType === 'menu') {
					const pages = res.data.pages || [];
					pages.forEach((page: any) => {
						options.push({
							id: page.moduleMetaId.replace('PAGE|', ''),
							label: (page.title?.en || page.title?.nl || page.pageId || page.moduleId) + ` (page)` ,
							type: 'page',
							parentModule: page.moduleId,
						});
					});
					const recipes = res.data.recipes || [];
					recipes.forEach((recipe: any) => {
						options.push({
							id: recipe.moduleMetaId.replace('RECIPE|', ''),
							label: (recipe.title?.en || recipe.title?.nl || recipe.recipeId || recipe.moduleId) + ` (recipe)` ,
							type: 'recipe',
							parentModule: recipe.moduleId,
						});
					});
				}
				setAvailableContents(options);
			})
			.finally(() => setLoadingContents(false));
		} else {
			setAvailableContents([]);
		}
	}, [selectedCareteam, selectedDisease, appContext.selectedProviderId, contentType]);

	// Fetch careteam hierarchy when careteam/disease/provider changes
	useEffect(() => {
		if (appContext.selectedProviderId) {
			setLoadingHierarchy(true);
					axios({
				url: process.env.REACT_APP_GRAPHQL_URL,
				method: 'POST',
				headers: {
				'Authorization': sessionStorage.getItem('adminToken') as string,
				'Content-Type': 'application/json',
				},
				data: {
				query: `query getCareteamHierarchy($providerId: String!, $careteamId: String, $diseaseId: String) {\n  getCareteamHierarchy(providerId: $providerId, careteamId: $careteamId, diseaseId: $diseaseId) {\n    careteams\n    diseases\n    patients\n  }\n}`,
				variables: {
					providerId: appContext.selectedProviderId,
					careteamId: selectedCareteam || undefined,
					diseaseId: selectedDisease || undefined,
				},
				},
			})
			.then((response: any) => {
				const data = response.data?.data?.getCareteamHierarchy || {};
				setCareteams(Array.isArray(data.careteams) ? data.careteams : []);
				setDiseases(Array.isArray(data.diseases) ? data.diseases : []);
				setPatients(Array.isArray(data.patients) ? data.patients : []);
			})
			.catch(() => {
				setCareteams([]);
				setDiseases([]);
				setPatients([]);
			})
			.finally(() => setLoadingHierarchy(false));
		}
	}, [appContext.selectedProviderId, selectedCareteam, selectedDisease]);

	// Handler for submitting the mutation
	const handlePushSubmit = async () => {
		setSubmitLoading(true);
		const mutation = pushType === 'careteam'
			? `mutation pushContentToCareteam($providerId: String!, $careteamId: String!, $diseaseId: String!, $contentId: String!) {\n  pushContentToCareteam(providerId: $providerId, careteamId: $careteamId, diseaseId: $diseaseId, contentId: $contentId)\n}`
			: `mutation pushContentToSpecificPatients($providerId: String!, $careteamId: String!, $diseaseId: String!, $contentId: String!, $patientIds: [String]!) {\n  pushContentToSpecificPatients(providerId: $providerId, careteamId: $careteamId, diseaseId: $diseaseId, contentId: $contentId, patientIds: $patientIds)\n}`;
		const variables: any = {
			providerId: appContext.selectedProviderId,
			careteamId: selectedCareteam,
			diseaseId: selectedDisease,
			contentId: selectedContentIdFull,
		};
		if (pushType === 'patients') {
			variables.patientIds = selectedPatients;
		}
		try {
			const response = await axios({
				url: process.env.REACT_APP_GRAPHQL_URL,
				method: 'POST',
				headers: {
					'Authorization': sessionStorage.getItem('adminToken') as string,
					'Content-Type': 'application/json',
				},
				data: {
					query: mutation,
					variables,
				},
			});

			// Check for GraphQL errors
			if (response.data && response.data.errors && response.data.errors.length > 0) {
				const errorMsg = response.data.errors.map((err: any) => err.message).join('; ');
				setSnackbar({open: true, message: `Push failed: ${errorMsg}`, severity: 'error'});
			} else {
				setSnackbar({open: true, message: 'Push successful!', severity: 'success'});
			}
		} catch (e: any) {
			let errorMsg = 'Push failed.';
			if (e.response && e.response.data && e.response.data.errors && e.response.data.errors.length > 0) {
				errorMsg = e.response.data.errors.map((err: any) => err.message).join('; ');
			} else if (e.message) {
				errorMsg = e.message;
			}
			setSnackbar({open: true, message: `Push failed: ${errorMsg}`, severity: 'error'});
		} finally {
			setSubmitLoading(false);
		}
	};

	return (
		<Box sx={{ maxWidth: 500 }}>
			<FormControl component="fieldset" sx={{ mb: 2 }}>
				<FormLabel component="legend">Push Type</FormLabel>
				<RadioGroup
					row
					value={pushType}
					onChange={e => setPushType(e.target.value as 'careteam' | 'patients')}
				>
					<FormControlLabel value="careteam" control={<Radio />} label="Careteam" />
					<FormControlLabel value="patients" control={<Radio />} label="Specific Patients" />
				</RadioGroup>
			</FormControl>
			<FormControl fullWidth sx={{ mb: 2 }}>
				<InputLabel id="careteam-label">Careteam</InputLabel>
				<Select
					labelId="careteam-label"
					value={selectedCareteam}
					label="Careteam"
					onChange={e => setSelectedCareteam(e.target.value)}
					disabled={loadingHierarchy}
				>
					{careteams.map(ct => (
						<MenuItem key={ct} value={ct}>{ct}</MenuItem>
					))}
				</Select>
			</FormControl>
			<FormControl fullWidth sx={{ mb: 2 }}>
				<Stack direction="row" alignItems="center" spacing={1}>
					<Select
						labelId="disease-label"
						value={selectedDisease}
						label="Disease"
						onChange={e => setSelectedDisease(e.target.value)}
						disabled={loadingHierarchy}
						sx={{ flex: 1 }}
					>
						{diseases.map(ds => (
							<MenuItem key={ds} value={ds}>{ds}</MenuItem>
						))}
					</Select>
					<Button size="small" onClick={() => setSelectedDisease('')} disabled={loadingHierarchy || !selectedDisease} variant="outlined">Clear</Button>
				</Stack>
			</FormControl>
			{pushType === 'patients' && (
				<FormControl fullWidth sx={{ mb: 2 }}>
					<Autocomplete
						multiple
						options={patients}
						value={selectedPatients}
						onChange={(_, value) => setSelectedPatients(value)}
						filterSelectedOptions
						loading={loadingHierarchy}
						disableCloseOnSelect
						getOptionLabel={option => option}
						renderInput={params => (
							<TextField {...params} label="Patients" variant="outlined" />
						)}
						renderOption={(props, option, { selected }) => (
							<li {...props} key={option}>
								<Checkbox
									style={{ marginRight: 8 }}
									checked={selected}
								/>
								{option}
							</li>
						)}
						isOptionEqualToValue={(option, value) => option === value}
						disabled={loadingHierarchy}
					/>
				</FormControl>
			)}
			<FormControl fullWidth sx={{ mb: 2 }}>
				<Box>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
						<Box sx={{ flex: 3 }}>
							<Autocomplete
								options={availableContents}
								loading={loadingContents}
								getOptionLabel={option => option.label}
								groupBy={option => {
									if (contentType === 'academy') {
										if (option.type === 'book') return '';
										if (option.type === 'chapter') {
											const parent = availableContents.find(b => b.id === option.parentBook);
											return parent ? `Chapters of: ${parent.label}` : 'Chapters';
										}
									} else if (contentType === 'menu') {
										if (option.type === 'page' || option.type === 'recipe') {
											return option.parentModule ? `Module: ${option.parentModule}` : 'Module: (unknown)';
										}
									}
									return '';
								}}
								value={availableContents.find(opt => opt.id === selectedContentId) || null}
								onChange={(_, value) => {
								if (!value) {
									setSelectedContentId('');
									setSelectedContentIdFull('');
									return;
								}
								setSelectedContentId(value.id || '');
								let contentIdFull = '';
								if (value.type === 'book') {
									contentIdFull = `MYACADEMY.BOOK.${value.id}`;
								} else if (value.type === 'chapter') {
									const parentBook = value.parentBook || '';
									contentIdFull = `MYACADEMY.BOOK.${parentBook}.CHAPTER.${value.id}`;
								} else if (value.type === 'page') {
									const parentModule = value.parentModule || '';
									contentIdFull = `MYMENU.MODULE.${parentModule}.PAGE.${value.id}`;
								} else if (value.type === 'recipe') {
									const parentModule = value.parentModule || '';
									contentIdFull = `MYMENU.MODULE.${parentModule}.RECIPE.${value.id}`;
								}
								setSelectedContentIdFull(contentIdFull);
							}}
								renderInput={params => (
									<TextField {...params} label="Content" variant="outlined" />
								)}
								isOptionEqualToValue={(option, value) => option.id === value.id}
								disabled={loadingHierarchy}
								renderOption={(props, option) => {
									let icon = '';
									let fontWeight = 400;
									let style: React.CSSProperties = {
										paddingLeft: option.type === 'chapter' || option.type === 'page' || option.type === 'recipe' ? 32 : 8,
										fontWeight,
									};
									if (contentType === 'academy') {
										if (option.type === 'book') {
											icon = '📚 ';
											fontWeight = 700;
											style.fontWeight = 700;
										} else if (option.type === 'chapter') {
											icon = '📄 ';
										} else if (option.type === 'no-chapter') {
											icon = '';
											style = {
												...style,
												color: '#888',
												fontStyle: 'italic',
												cursor: 'not-allowed',
											};
										}
									} else if (contentType === 'menu') {
										if (option.type === 'page') {
											icon = '📄 ';
											style.paddingLeft = 32;
										} else if (option.type === 'recipe') {
											icon = '🍽️ ';
											style.paddingLeft = 32;
										}
									}
									return (
										<li {...props} style={style}>
											{icon}{option.label}
										</li>
									);
								}}
								getOptionDisabled={option => option.disabled === true}
								sx={{ minWidth: 580 }}
							/>
						</Box>
						<ToggleButtonGroup
							value={contentType}
							exclusive
							onChange={(_e, val) => {
								if (val) {
									setContentType(val);
									setSelectedContentId('');
								}
							}}
							sx={{ height: 56, whiteSpace: 'nowrap' }}
						>
							<ToggleButton value="academy" sx={{ fontWeight: 700, px: 3 }}>
								MyAcademy
							</ToggleButton>
							<ToggleButton value="menu" sx={{ fontWeight: 700, px: 3 }}>
								MyMenu
							</ToggleButton>
						</ToggleButtonGroup>
					</Box>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
						<Box sx={{
							border: 2,
							borderColor: 'primary.main',
							borderRadius: 2,
							px: 2,
							py: 1.5,
							minHeight: 56,
							minWidth: 600,
							bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
							boxShadow: 2,
							paddingBottom:  '18px',
						}}>
							<Typography variant="caption" color="primary" sx={{ mb: 0.5, fontWeight: 700, letterSpacing: 1 }}>
								Content ID
							</Typography>
							<Box
								sx={{
									fontFamily: 'monospace',
									fontSize: 15,
									fontWeight: 700,
									color: 'primary.dark',
									background: '#fff',
									borderRadius: 1,
									px: 1.5,
									py: 0.5,
									wordBreak: 'break-all',
									letterSpacing: 1,
									border: '1px solid',
									borderColor: 'primary.light',
									boxShadow: 1,
									display: 'inline-block',
									maxWidth: '100%',
									mt: 0.5,
								}}
							>
								{(() => {
									return selectedContentIdFull;
								})()}
							</Box>
						</Box>
						<Button
							variant="contained"
							color="primary"
							onClick={handlePushSubmit}
							disabled={submitLoading || !selectedCareteam || !selectedDisease || !selectedContentId || (pushType === 'patients' && selectedPatients.length === 0)}
							sx={{ height: 56, minWidth: 120, fontWeight: 700 }}
						>
							{submitLoading ? 'Pushing...' : 'Push'}
						</Button>
					</Box>
				</Box>
			</FormControl>
			<Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({...snackbar, open: false})}>
				<Alert onClose={() => setSnackbar({...snackbar, open: false})} severity={snackbar.severity} sx={{ width: '100%' }}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default PushContentsTab;
